import {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import { UserModel } from "../Api/Models/user.mode";
import { apiEndpointStorageKey } from "../Api/request";
import { userService } from "../Api/Services/UserService";
import { useLoading } from "./LoadingProvider";
import {patrollerService} from "../Api/Services/PatrollerService";
import GPSConsentModal from "../Components/GPSConsentModal";

interface UserContextInterface {
    user: UserModel | null;
    login: CallableFunction;
    logout: CallableFunction;
    setUser: (user: UserModel) => void;
}

interface UserProviderPropsInterface {
    children: JSX.Element;
}

export const authTokenStorageKey = "apiToken";

const UserContext = createContext<UserContextInterface>({
    user: null,
    login: () => {},
    logout: () => {},
    setUser: () => {},
});

export const UserProvider = ({ children }: UserProviderPropsInterface) => {
    const [user, setUser] = useState<UserModel | null>(null);

    const [permissionRequested, setPermissionRequested] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);

    const trackingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isPromptingRef = useRef<boolean>(false);

    const { startLoading, finnishLoading } = useLoading();

    const sendLocationUpdate = async (latitude: number, longitude: number) => {
        if (!localStorage.getItem(authTokenStorageKey)) return;

        try {
            await patrollerService.savePatrollerLocation(latitude, longitude);
        } catch (err) {
            console.error("Failed to save location", err);
        }
    };

    const startTracking = useCallback(() => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                sendLocationUpdate(pos.coords.latitude, pos.coords.longitude);
            },
            (err) => {
                console.error("startTracking initial error", err);
            }
        );

        if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);

        trackingIntervalRef.current = setInterval(() => {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    sendLocationUpdate(pos.coords.latitude, pos.coords.longitude);
                },
                (error) => {
                    console.error("Location tracking error", error);
                }
            );
        }, 300000);
    }, []);

    const attemptNativePrompt = useCallback(() => {
        if (!("geolocation" in navigator)) {
            console.warn("Geolocation is not supported by this browser.");
            setPermissionRequested(true);
            return;
        }

        isPromptingRef.current = true;
        setPermissionRequested(true);
        setShowModal(false);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                console.log("Location access granted (native prompt)");
                isPromptingRef.current = false;
                startTracking();
            },
            (error) => {
                console.error("Location access denied or failed (native prompt):", error);
                isPromptingRef.current = false;
                setTimeout(() => {
                    setShowModal(true);
                }, 250);
            }
        );
    }, [startTracking]);

    const nativePromptDirect = useCallback(() => {
        if (!("geolocation" in navigator)) {
            console.warn("Geolocation is not supported by this browser.");
            setPermissionRequested(true);
            return;
        }

        console.log("nativePromptDirect invoked (user gesture)"); // debug
        try {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    console.log("Location access granted (user gesture)");
                    setPermissionRequested(true);
                    setShowModal(false);
                    startTracking();
                },
                (error) => {
                    console.error("Location access denied or failed (user gesture):", error);
                    setPermissionRequested(true);
                    if (error && (error.code === 1 || error.code === error.PERMISSION_DENIED)) {
                        console.warn("Permission denied — show instructions to user to enable in Safari settings");
                        setShowModal(true);
                    } else {
                        setShowModal(true);
                    }
                },
                { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
            );
        } catch (err) {
            console.error("Geolocation call failed synchronously:", err);
            setShowModal(true);
        }
    }, [startTracking]);

    const checkPermission = useCallback(async () => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        if (isIOS) {
            return;
        }

        if (navigator.permissions && navigator.permissions.query) {
            try {
                const result = await navigator.permissions.query({ name: "geolocation" as PermissionName });
                console.log("Permission state (query):", result.state);

                const handleState = (state: PermissionState) => {
                    if (state === "granted") {
                        setPermissionRequested(true);
                        setShowModal(false);
                        startTracking();
                    } else if (state === "prompt") {
                        setPermissionRequested(false);
                        if (!permissionRequested && !isPromptingRef.current) {
                            attemptNativePrompt();
                        }
                    } else if (state === "denied") {
                        setPermissionRequested(true);
                        setShowModal(true);
                        if (trackingIntervalRef.current) {
                            clearInterval(trackingIntervalRef.current);
                            trackingIntervalRef.current = null;
                        }
                    }
                };

                handleState(result.state);

                result.onchange = () => {
                    console.log("Permission onchange:", result.state);
                    handleState(result.state);
                };
            } catch (err) {
                console.error("Permission query failed:", err);
                if (!permissionRequested) {
                    attemptNativePrompt();
                }
            }
        }
    }, [attemptNativePrompt, permissionRequested, startTracking]);

    useEffect(() => {
        if (!user) {
            if (trackingIntervalRef.current) {
                clearInterval(trackingIntervalRef.current);
                trackingIntervalRef.current = null;
            }
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
            }
            setPermissionRequested(false);
            setShowModal(false);
            return;
        }

        console.log("User logged in, starting location monitoring...");
        checkPermission();

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
            setShowModal(true);
        }

        checkIntervalRef.current = setInterval(checkPermission, 600000);

        return () => {
            if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);
            if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
        };
        // eslint-disable-next-line
    }, [user]);

    useEffect(() => {
        console.log('useEffect');
        startLoading("get-cookies-faszomat-az-egeszbe");
        userService
            .getCookies()
            .then(() => {
                if (localStorage.getItem(authTokenStorageKey)) {
                    startLoading("get-current-user");

                    userService
                        .getCurrentUser()
                        .then((user) => {
                            setUser(user.user);
                        })
                        .finally(() => {
                            finnishLoading("get-current-user");
                        });
                }
            })
            .finally(() => {
                finnishLoading("get-cookies-faszomat-az-egeszbe");
            });
        //eslint-disable-next-line
    }, []);

    const logout = async () => {
        try { await userService.logout(); } catch {}
        [
            authTokenStorageKey,
            apiEndpointStorageKey,
            'isFirstLogin',
            'gearReport',
            'historicalGear',
            'user',
        ].forEach(k => localStorage.removeItem(k));
        setUser(null);
    };

    const login = async (email: string, password: string) => {
        return userService.login(email, password).then((userToken) => {
            console.log(userToken);
            localStorage.setItem(authTokenStorageKey, userToken.token);
            localStorage.setItem(apiEndpointStorageKey, userToken.endpoint);
            localStorage.setItem('isFirstLogin', userToken.user.isFirstLogin !== undefined && userToken.user.isFirstLogin === '1' ? '1' : '0');
            localStorage.setItem('gearReport', userToken.user.gearReport ? '1' : '0');
            localStorage.setItem('historicalGear', userToken.user.historicalGear ? '1' : '0');
            setUser(userToken.user);
        });
    };

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    return (
        <UserContext.Provider value={{ user, login, logout, setUser }}>
            {children}
            {user && showModal && !isSafari && (
                <GPSConsentModal
                    onApprove={() => {
                        setShowModal(false);
                        attemptNativePrompt();
                    }}
                />
            )}
            {user && showModal && isSafari && (
                <GPSConsentModal
                    onApprove={() => {
                        setShowModal(false);
                        nativePromptDirect();
                    }}
                />
            )}
        </UserContext.Provider>
    );
};

export default UserContext;

export function useUser() {
    return useContext(UserContext);
}
